import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import {
  Heading,
  Text,
  Stack,
  Container,
} from '@/components/ui/index.js';
import { BottomNav } from '@/components/navigation/index.js';
import { SportFilter } from './components/SportFilter/index.js';
import { EventCard } from './components/EventCard/index.js';
import { VenueCard } from './components/VenueCard/index.js';
import { useDiscoveryStore } from '../../store/useDiscoveryStore.js';
import styles from './DiscoveryPage.module.css';

/**
 * DiscoveryPage - Main activity feed with personalized recommendations
 * 
 * Features sport category filters, featured events, nearby venues,
 * and recommended content. This is the primary home for authenticated users.
 */
export function DiscoveryPage() {
  const navigate = useNavigate();
  const { 
    selectedSport, 
    setSelectedSport, 
    featuredEvents, 
    nearbyVenues,
    recommendedEvents,
    hasNotification,
  } = useDiscoveryStore();
  
  const [isLoading, setIsLoading] = useState(true);

  // Simulate initial loading
  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 300);
    return () => clearTimeout(timer);
  }, []);

  // Get greeting based on time of day
  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  }, []);

  // Filter events based on selected sport
  const filteredFeaturedEvents = useMemo(() => {
    if (selectedSport === 'all') return featuredEvents;
    return featuredEvents.filter(event => event.sport === selectedSport);
  }, [featuredEvents, selectedSport]);

  const filteredRecommendedEvents = useMemo(() => {
    if (selectedSport === 'all') return recommendedEvents;
    return recommendedEvents.filter(event => event.sport === selectedSport);
  }, [recommendedEvents, selectedSport]);

  const handleEventClick = (eventId) => {
    navigate(`/events/${eventId}`);
  };

  const handleVenueClick = (venueId) => {
    navigate(`/venues/${venueId}`);
  };

  const handleSeeAllEvents = () => {
    navigate('/events');
  };

  const handleSeeAllVenues = () => {
    navigate('/venues');
  };

  if (isLoading) {
    return (
      <div className={styles.page}>
        <Container size="full" padding className={styles.loadingContainer}>
          <div className={styles.loadingPulse} />
        </Container>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <Container size="full" padding>
          <Stack direction="row" justify="space-between" align="center">
            <div className={styles.headerContent}>
              <Text size="sm" color="secondary" className={styles.greeting}>
                {greeting}, Athlete
              </Text>
              <Heading level={1} size="lg" color="primary">
                Discover
              </Heading>
            </div>
            <button 
              className={styles.notificationButton}
              aria-label="Notifications"
            >
              <Bell size={24} strokeWidth={2} />
              {hasNotification && <span className={styles.notificationBadge} />}
            </button>
          </Stack>
        </Container>
      </header>

      {/* Main Content */}
      <main className={styles.main}>
        <Container size="full" padding>
          {/* Sport Category Filters */}
          <section className={styles.section}>
            <SportFilter 
              selected={selectedSport}
              onSelect={setSelectedSport}
            />
          </section>

          {/* Featured Events */}
          <section className={styles.section}>
            <Stack direction="row" justify="space-between" align="center" className={styles.sectionHeader}>
              <Heading level={2} size="md" color="primary">
                Featured Events
              </Heading>
              <button 
                className={styles.seeAllButton}
                onClick={handleSeeAllEvents}
              >
                See All
              </button>
            </Stack>
            
            <div className={styles.horizontalScroll}>
              <div className={styles.featuredEventsList}>
                {filteredFeaturedEvents.map((event, index) => (
                  <EventCard 
                    key={event.id}
                    event={event}
                    variant="featured"
                    style={{ animationDelay: `${index * 100}ms` }}
                    onClick={() => handleEventClick(event.id)}
                  />
                ))}
                {filteredFeaturedEvents.length === 0 && (
                  <div className={styles.emptyState}>
                    <Text size="sm" color="muted">
                      No featured events for this sport
                    </Text>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Nearby Venues */}
          <section className={styles.section}>
            <Stack direction="row" justify="space-between" align="center" className={styles.sectionHeader}>
              <Heading level={2} size="md" color="primary">
                Nearby Venues
              </Heading>
              <button 
                className={styles.seeAllButton}
                onClick={handleSeeAllVenues}
              >
                See All
              </button>
            </Stack>
            
            <div className={styles.horizontalScroll}>
              <div className={styles.venuesList}>
                {nearbyVenues.map((venue, index) => (
                  <VenueCard 
                    key={venue.id}
                    venue={venue}
                    style={{ animationDelay: `${index * 100}ms` }}
                    onClick={() => handleVenueClick(venue.id)}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* Recommended For You */}
          <section className={`${styles.section} ${styles.recommendedSection}`}>
            <Stack direction="row" justify="space-between" align="center" className={styles.sectionHeader}>
              <Heading level={2} size="md" color="primary">
                Recommended For You
              </Heading>
            </Stack>
            
            <div className={styles.recommendedList}>
              {filteredRecommendedEvents.map((event, index) => (
                <EventCard 
                  key={event.id}
                  event={event}
                  variant="compact"
                  style={{ animationDelay: `${index * 100}ms` }}
                  onClick={() => handleEventClick(event.id)}
                />
              ))}
              {filteredRecommendedEvents.length === 0 && (
                <div className={styles.emptyState}>
                  <Text size="sm" color="muted">
                    No recommendations for this sport
                  </Text>
                </div>
              )}
            </div>
          </section>
        </Container>
      </main>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
}

export default DiscoveryPage;
